import { useEffect, useMemo, useRef, useState } from "react";
import { HiddenLeafGlyph, LeafClusterPiece } from "./HiddenLeafGlyph";

interface Piece {
  id: string;
  x: number;
  y: number;
  targetX: number;
  targetY: number;
  rotation: number;
  targetRotation: number;
  placed: boolean;
}

const STAGES = [
  {
    character: "义",
    assist: "完整骨架 · 自动转叶",
    hint: "让一叶、个叶、介叶依次贴近“义”的三段笔势。",
    targets: [[188, 92, -18], [180, 196, 42], [204, 278, 0]],
  },
  {
    character: "信",
    assist: "局部骨架 · 弱吸附",
    hint: "亻旁先立，言字的叶再向右展开。",
    targets: [[88, 198, -45], [212, 132, 0], [218, 264, 0]],
  },
  {
    character: "志",
    assist: "只示起笔 · 自辨方向",
    hint: "先定士字上端，再让叶势落入心字底。",
    targets: [[182, 96, 0], [178, 184, 0], [182, 278, 45]],
  },
] as const;

const START_PIECES: Piece[] = [
  { id: "one", x: 55, y: 365, targetX: 0, targetY: 0, rotation: 0, targetRotation: 0, placed: false },
  { id: "ge", x: 180, y: 365, targetX: 0, targetY: 0, rotation: 0, targetRotation: 0, placed: false },
  { id: "jie", x: 305, y: 365, targetX: 0, targetY: 0, rotation: 0, targetRotation: 0, placed: false },
];

interface LeafPuzzleProps {
  initialStage?: number;
  onProgress: (stage: number) => void;
  onComplete: () => void;
}

function stagePieces(stage: number): Piece[] {
  return START_PIECES.map((piece, index) => ({
    ...piece,
    targetX: STAGES[stage].targets[index][0],
    targetY: STAGES[stage].targets[index][1],
    targetRotation: STAGES[stage].targets[index][2],
    rotation: stage === 0 ? STAGES[stage].targets[index][2] : 0,
  }));
}

export function LeafPuzzle({ initialStage = 0, onProgress, onComplete }: LeafPuzzleProps) {
  const [stage, setStage] = useState(Math.min(initialStage, 2));
  const [pieces, setPieces] = useState(() => stagePieces(Math.min(initialStage, 2)));
  const [selected, setSelected] = useState("one");
  const [failures, setFailures] = useState(0);
  const [showHint, setShowHint] = useState(false);
  const [revealWord, setRevealWord] = useState(false);
  const boardRef = useRef<HTMLDivElement>(null);
  const dragRef = useRef<{ id: string; pointerId: number } | null>(null);
  const current = STAGES[stage];
  const complete = pieces.every((piece) => piece.placed);

  useEffect(() => {
    const timer = window.setTimeout(() => setShowHint(true), 8_000);
    return () => window.clearTimeout(timer);
  }, [stage]);

  const placedCount = useMemo(() => pieces.filter((piece) => piece.placed).length, [pieces]);

  const movePiece = (id: string, x: number, y: number) => {
    setPieces((currentPieces) =>
      currentPieces.map((piece) => (piece.id === id && !piece.placed ? { ...piece, x, y } : piece)),
    );
  };

  const finishDrag = (id: string) => {
    setPieces((currentPieces) =>
      currentPieces.map((piece) => {
        if (piece.id !== id || piece.placed) return piece;
        const distance = Math.hypot(piece.x - piece.targetX, piece.y - piece.targetY);
        const rotationDifference = Math.abs(((piece.rotation - piece.targetRotation + 180) % 360) - 180);
        const canSnap = distance < (stage === 0 ? 78 : 54) && (stage === 0 || rotationDifference <= 48);
        if (!canSnap) {
          setFailures((value) => value + 1);
          return piece;
        }
        return {
          ...piece,
          x: piece.targetX,
          y: piece.targetY,
          rotation: piece.targetRotation,
          placed: true,
        };
      }),
    );
  };

  const onPointerMove = (event: React.PointerEvent<HTMLButtonElement>) => {
    if (!dragRef.current || dragRef.current.pointerId !== event.pointerId || !boardRef.current) return;
    const rect = boardRef.current.getBoundingClientRect();
    movePiece(
      dragRef.current.id,
      ((event.clientX - rect.left) / rect.width) * 360,
      ((event.clientY - rect.top) / rect.height) * 420,
    );
  };

  const rotateSelected = () => {
    setPieces((currentPieces) =>
      currentPieces.map((piece) =>
        piece.id === selected && !piece.placed ? { ...piece, rotation: (piece.rotation + 45) % 360 } : piece,
      ),
    );
  };

  const listenToWind = () => {
    setPieces((currentPieces) =>
      currentPieces.map((piece) => ({
        ...piece,
        x: piece.targetX,
        y: piece.targetY,
        rotation: piece.targetRotation,
        placed: true,
      })),
    );
  };

  const nextStage = () => {
    if (stage === STAGES.length - 1) {
      onProgress(STAGES.length);
      onComplete();
      return;
    }
    const next = stage + 1;
    setStage(next);
    setPieces(stagePieces(next));
    setFailures(0);
    setShowHint(false);
    setRevealWord(false);
    onProgress(next);
  };

  return (
    <div className="leaf-puzzle">
      <div className="puzzle-heading">
        <span>第 {stage + 1} 字 / 3</span>
        <strong>{current.assist}</strong>
      </div>
      <div
        className={`puzzle-board ${complete ? "is-complete" : ""} ${revealWord ? "is-word-revealed" : ""}`}
        ref={boardRef}
        aria-label={`用竹叶拼出“${current.character}”字`}
      >
        <HiddenLeafGlyph
          character={current.character}
          visibleGroups={pieces.flatMap((piece, index) => (piece.placed ? [index] : []))}
          guideGroups={stage === 0 ? [0, 1, 2] : stage === 1 ? [0] : []}
          reveal={revealWord}
          className="puzzle-hidden-glyph"
          labelled={false}
        />
        {stage === 2 && <span className="start-mark" aria-hidden="true" />}
        {pieces.map((piece) => (
          <button
            type="button"
            key={piece.id}
            className={`leaf-piece leaf-piece--${piece.id} ${piece.placed ? "is-placed" : ""}`}
            style={{
              left: `${(piece.x / 360) * 100}%`,
              top: `${(piece.y / 420) * 100}%`,
              transform: `translate(-50%, -50%) rotate(${piece.rotation}deg)`,
            }}
            aria-label={`${piece.id}叶组${piece.placed ? "已归位" : "，可拖动"}`}
            aria-pressed={selected === piece.id}
            onClick={() => setSelected(piece.id)}
            onPointerDown={(event) => {
              if (piece.placed) return;
              setSelected(piece.id);
              dragRef.current = { id: piece.id, pointerId: event.pointerId };
              event.currentTarget.setPointerCapture(event.pointerId);
            }}
            onPointerMove={onPointerMove}
            onPointerUp={(event) => {
              if (!dragRef.current) return;
              finishDrag(dragRef.current.id);
              dragRef.current = null;
              event.currentTarget.releasePointerCapture(event.pointerId);
            }}
          >
            <LeafClusterPiece kind={piece.id as "one" | "ge" | "jie"} />
            <span className="leaf-piece-name">{piece.id === "one" ? "一叶" : piece.id === "ge" ? "个叶" : "介叶"}</span>
          </button>
        ))}
        {complete && (
          <button type="button" className="word-reveal" onClick={() => setRevealWord((value) => !value)}>
            {revealWord ? "画态" : "字态"} · {current.character}
          </button>
        )}
      </div>
      <div className="puzzle-controls">
        <button type="button" className="button button--quiet" onClick={rotateSelected} disabled={complete || stage === 0}>
          转叶 45°
        </button>
        {(showHint || failures >= 3) && !complete && (
          <button type="button" className="button button--ink" onClick={listenToWind}>
            听风归位
          </button>
        )}
      </div>
      <p className="puzzle-status" aria-live="polite">
        {complete
          ? `叶片安静归位。“${current.character}”从竹影里显出。`
          : showHint || failures >= 3
            ? `${current.hint} 已归位 ${placedCount}/3。`
            : `拖动叶片组，点“转叶”调整方向。已归位 ${placedCount}/3。`}
      </p>
      {complete && (
        <button type="button" className="button button--primary" onClick={nextStage}>
          {stage === STAGES.length - 1 ? "看二十字全诗" : "解下一字"}
        </button>
      )}
    </div>
  );
}
